if (!customElements.get("el-product-info")) {
  customElements.define(
    "el-product-info",
    class ElProductInfo extends HTMLElement {
      connectedCallback() {
        if (this.dataset.ready === "true") return;
        this.dataset.ready = "true";

        this.activePopup = null;
        this.lastFocusedElement = null;

        this.handleClick = this.handleClick.bind(this);
        this.handleKeydown = this.handleKeydown.bind(this);

        this.addEventListener("click", this.handleClick);
        document.addEventListener("keydown", this.handleKeydown);
      }

      disconnectedCallback() {
        this.removeEventListener("click", this.handleClick);
        document.removeEventListener("keydown", this.handleKeydown);
      }

      handleClick(event) {
        const opener = event.target.closest("[data-el-popup-open]");

        if (opener && this.contains(opener)) {
          this.openPopup(opener.dataset.elPopupOpen, opener);
          return;
        }

        const closer = event.target.closest("[data-el-popup-close]");

        if (closer && this.activePopup?.contains(closer)) {
          this.closePopup();
        }
      }

      handleKeydown(event) {
        if (!this.activePopup) return;

        if (event.key === "Escape") {
          event.preventDefault();
          this.closePopup();
          return;
        }

        if (event.key !== "Tab") return;

        const focusable = this.getFocusableElements(this.activePopup);

        if (!focusable.length) {
          event.preventDefault();

          this.activePopup
            .querySelector("[data-el-popup-dialog]")
            ?.focus({ preventScroll: true });

          return;
        }

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }

      openPopup(id, opener) {
        const popup = this.querySelector(`#${CSS.escape(id)}`);

        if (!popup) return;

        if (this.activePopup && this.activePopup !== popup) {
          this.closePopup(false);
        }

        this.activePopup = popup;
        this.lastFocusedElement = opener || document.activeElement;

        popup.hidden = false;
        document.body.classList.add("el-product-popup-is-open");

        requestAnimationFrame(() => {
          const focusTarget =
            popup.querySelector("[data-el-popup-close]") ||
            popup.querySelector("[data-el-popup-dialog]");

          focusTarget?.focus({ preventScroll: true });
        });
      }

      closePopup(restoreFocus = true) {
        if (!this.activePopup) return;

        this.activePopup.hidden = true;
        this.activePopup = null;

        document.body.classList.remove("el-product-popup-is-open");

        if (restoreFocus) {
          this.lastFocusedElement?.focus?.({ preventScroll: true });
        }
      }

      getFocusableElements(container) {
        return Array.from(
          container.querySelectorAll(
            'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
          ),
        ).filter((element) => !element.hasAttribute("hidden"));
      }
    },
  );
}

if (!customElements.get("el-form-atc")) {
  customElements.define(
    "el-form-atc",
    class ElFormAtc extends HTMLElement {
      connectedCallback() {
        if (this.dataset.ready === "true") return;
        this.dataset.ready = "true";

        this.form = this.querySelector("[data-el-product-form]");
        this.button = this.querySelector("[data-el-atc-button]");
        this.label = this.querySelector("[data-el-atc-label]");
        this.price = this.querySelector("[data-el-atc-price]");
        this.status = this.querySelector("[data-el-atc-status]");
        this.variantInput = this.querySelector("[data-el-variant-id]");

        this.variants = this.parseVariants();

        this.currentVariant = this.variants.find(
          (variant) => String(variant.id) === String(this.variantInput?.value),
        );

        this.productSection = this.closest(".shopify-section") || document;

        if (!this.form || !this.button || !this.variantInput) return;

        this.handleSubmit = this.handleSubmit.bind(this);
        this.handleVariantInputChange =
          this.handleVariantInputChange.bind(this);
        this.handleVariantEvent = this.handleVariantEvent.bind(this);

        this.form.addEventListener("submit", this.handleSubmit);

        this.productSection.addEventListener(
          "change",
          this.handleVariantInputChange,
        );

        document.addEventListener("variant:change", this.handleVariantEvent);

        document.addEventListener(
          "product:variant-change",
          this.handleVariantEvent,
        );
      }

      disconnectedCallback() {
        this.form?.removeEventListener("submit", this.handleSubmit);

        this.productSection?.removeEventListener(
          "change",
          this.handleVariantInputChange,
        );

        document.removeEventListener("variant:change", this.handleVariantEvent);

        document.removeEventListener(
          "product:variant-change",
          this.handleVariantEvent,
        );
      }

      parseVariants() {
        const script = this.querySelector("[data-el-product-variants]");

        if (!script) return [];

        try {
          return JSON.parse(script.textContent || "[]");
        } catch (error) {
          console.warn(
            "ElFormAtc: product variants JSON could not be parsed.",
            error,
          );

          return [];
        }
      }

      handleVariantInputChange(event) {
        const input = event.target.closest('[name="id"]');

        if (
          !input ||
          input === this.variantInput ||
          !this.productSection.contains(input)
        ) {
          return;
        }

        this.setVariant(input.value);
      }

      handleVariantEvent(event) {
        const variant = event.detail?.variant || event.detail;

        if (!variant?.id) return;

        const sourceSection = event.target?.closest?.(".shopify-section");

        if (sourceSection && sourceSection !== this.productSection) {
          return;
        }

        this.setVariant(variant.id, variant);
      }

      setVariant(id, suppliedVariant = null) {
        const variant =
          suppliedVariant ||
          this.variants.find((item) => String(item.id) === String(id));

        this.variantInput.value = id;

        if (!variant) return;

        this.currentVariant = variant;

        this.button.disabled = !variant.available;

        this.button.setAttribute("aria-disabled", String(!variant.available));

        if (!variant.available) {
          this.label.textContent =
            this.button.dataset.soldOutLabel || "Sold out";

          return;
        }

        const defaultLabel = this.button.dataset.defaultLabel || "Add to cart";

        const formattedPrice = this.formatMoney(variant.price);

        this.label.innerHTML = `${this.escapeHtml(
          defaultLabel,
        )} · <span data-el-atc-price>${this.escapeHtml(formattedPrice)}</span>`;

        this.price = this.querySelector("[data-el-atc-price]");

        const productPrice = this.productSection.querySelector(
          "[data-el-current-price]",
        );

        if (productPrice) {
          productPrice.textContent = formattedPrice;
        }
      }

      async handleSubmit(event) {
        event.preventDefault();

        if (this.button.disabled || this.classList.contains("is-loading")) {
          return;
        }

        this.setLoading(true);
        this.setStatus("");

        const formData = new FormData(this.form);

        let cartDrawer = document.querySelector("cart-drawer");

        const cartDrawerSectionId = this.getCartDrawerSectionId(cartDrawer);

        /*
         * Request the refreshed cart drawer HTML together with the
         * add-to-cart request.
         */
        if (cartDrawerSectionId) {
          formData.delete("sections");
          formData.delete("sections_url");

          formData.append("sections", cartDrawerSectionId);
          formData.append("sections_url", window.location.pathname);
        }

        try {
          const response = await fetch(this.getCartAddUrl(), {
            method: "POST",
            headers: {
              Accept: "application/json",
              "X-Requested-With": "XMLHttpRequest",
            },
            body: formData,
          });

          const result = await response.json();

          if (!response.ok || result.status) {
            throw new Error(
              result.description ||
                result.message ||
                "This product could not be added to the cart.",
            );
          }

          /*
           * Use the theme's native renderContents method when available.
           */
          if (
            cartDrawer &&
            typeof cartDrawer.renderContents === "function" &&
            result.sections
          ) {
            cartDrawer.renderContents(result);
          } else if (
            cartDrawer &&
            cartDrawerSectionId &&
            result.sections?.[cartDrawerSectionId]
          ) {
            /*
             * Otherwise replace the slotted cart drawer content manually.
             */
            this.renderCartDrawerSection(
              cartDrawer,
              result.sections[cartDrawerSectionId],
            );
          } else {
            /*
             * Final fallback: request the cart drawer section separately.
             */
            cartDrawer = await this.refreshCartDrawer(cartDrawer);
          }

          const cart = await this.refreshCartCount();

          /*
           * Find the drawer again because the theme may have replaced it.
           */
          cartDrawer = document.querySelector("cart-drawer") || cartDrawer;

          this.openCartDrawer(cartDrawer);

          document.dispatchEvent(
            new CustomEvent("cart:updated", {
              detail: {
                cart,
                item: result,
                source: "el-form-atc",
              },
            }),
          );

          document.dispatchEvent(
            new CustomEvent("cart:refresh", {
              detail: {
                cart,
                item: result,
                source: "el-form-atc",
                openDrawer: true,
              },
            }),
          );

          document.dispatchEvent(
            new CustomEvent("cartQuantityUpdated", {
              detail: {
                cart,
                item: result,
                source: "el-form-atc",
              },
            }),
          );

          document.dispatchEvent(
            new CustomEvent("product:added", {
              detail: {
                cart,
                item: result,
                source: "el-form-atc",
              },
            }),
          );

          this.dispatchEvent(
            new CustomEvent("el:cart-added", {
              bubbles: true,
              detail: {
                cart,
                item: result,
              },
            }),
          );

          this.showAddedState();
        } catch (error) {
          console.error("ElFormAtc:", error);
          this.setStatus(error.message, true);
        } finally {
          this.setLoading(false);
        }
      }

      getCartDrawerSectionId(cartDrawer) {
        if (!cartDrawer) return "";

        const sectionElement = cartDrawer.closest(".shopify-section");

        const directSectionId =
          cartDrawer.dataset.sectionId ||
          sectionElement?.dataset.sectionId ||
          sectionElement?.id?.replace(/^shopify-section-/, "");

        if (directSectionId) {
          return directSectionId;
        }

        /*
         * Recover a section ID from an element such as:
         *
         * cart-note-sections--27265559200125__cart-drawer
         */
        const elementWithSectionId = cartDrawer.querySelector(
          '[id*="__cart-drawer"]',
        );

        const sectionIdMatch = elementWithSectionId?.id?.match(
          /sections--[^\s]+?__cart-drawer/,
        );

        return sectionIdMatch?.[0] || "";
      }

      renderCartDrawerSection(cartDrawer, sectionHtml) {
        if (!cartDrawer || !sectionHtml) {
          return cartDrawer;
        }

        const parsedDocument = new DOMParser().parseFromString(
          sectionHtml,
          "text/html",
        );

        const refreshedDrawer = parsedDocument.querySelector("cart-drawer");

        if (!refreshedDrawer) {
          return cartDrawer;
        }

        /*
         * Keep the current custom element and its shadow root.
         * Replace only its light DOM/slotted content.
         */
        cartDrawer.innerHTML = refreshedDrawer.innerHTML;

        return cartDrawer;
      }

      async refreshCartDrawer(existingDrawer = null) {
        const cartDrawer =
          existingDrawer || document.querySelector("cart-drawer");

        const sectionId = this.getCartDrawerSectionId(cartDrawer);

        if (!cartDrawer || !sectionId) {
          return cartDrawer;
        }

        try {
          const url = new URL(window.location.href);

          url.search = "";
          url.hash = "";

          url.searchParams.set("section_id", sectionId);

          const response = await fetch(url.toString(), {
            headers: {
              Accept: "text/html",
              "X-Requested-With": "XMLHttpRequest",
            },
          });

          if (!response.ok) {
            return cartDrawer;
          }

          const sectionHtml = await response.text();

          return this.renderCartDrawerSection(cartDrawer, sectionHtml);
        } catch (error) {
          console.warn("ElFormAtc: cart drawer refresh failed.", error);

          return cartDrawer;
        }
      }

      openCartDrawer(cartDrawer) {
        if (!cartDrawer) return;

        /*
         * Support a theme-specific drawer method when one exists.
         */
        if (typeof cartDrawer.show === "function") {
          cartDrawer.show();
          return;
        }

        if (typeof cartDrawer.showDrawer === "function") {
          cartDrawer.showDrawer();
          return;
        }

        /*
         * Do not call cartDrawer.open().
         *
         * In this theme, "open" is an HTML attribute/state,
         * not a JavaScript method.
         */
        cartDrawer.style.display = "block";
        cartDrawer.setAttribute("open", "");
        cartDrawer.setAttribute("aria-hidden", "false");

        /*
         * Emit events in case the theme listens for drawer events.
         */
        cartDrawer.dispatchEvent(
          new CustomEvent("drawer:open", {
            bubbles: true,
            detail: {
              source: "el-form-atc",
            },
          }),
        );

        document.dispatchEvent(
          new CustomEvent("cart-drawer:open", {
            detail: {
              source: "el-form-atc",
            },
          }),
        );
      }

      async refreshCartCount() {
        try {
          const root = window.Shopify?.routes?.root || "/";

          const response = await fetch(`${root}cart.js`, {
            headers: {
              Accept: "application/json",
            },
          });

          if (!response.ok) {
            return null;
          }

          const cart = await response.json();

          document
            .querySelectorAll("[data-el-cart-count], [data-cart-count]")
            .forEach((element) => {
              element.textContent = cart.item_count;
              element.hidden = cart.item_count === 0;

              element.classList.toggle("is-hidden", cart.item_count === 0);
            });

          return cart;
        } catch (error) {
          console.warn("ElFormAtc: cart count refresh failed.", error);

          return null;
        }
      }

      showAddedState() {
        const addedLabel = this.button.dataset.addedLabel || "Added";

        const currentVariant = this.variants.find(
          (variant) => String(variant.id) === String(this.variantInput.value),
        );

        const defaultLabel = this.button.dataset.defaultLabel || "Add to cart";

        const currentPrice = currentVariant
          ? this.formatMoney(currentVariant.price)
          : this.price?.textContent?.trim();

        this.label.textContent = addedLabel;

        window.clearTimeout(this.addedStateTimer);

        this.addedStateTimer = window.setTimeout(() => {
          this.label.innerHTML = `${this.escapeHtml(defaultLabel)}${
            currentPrice
              ? ` · <span data-el-atc-price>${this.escapeHtml(
                  currentPrice,
                )}</span>`
              : ""
          }`;

          this.price = this.querySelector("[data-el-atc-price]");
        }, 1400);
      }

      setLoading(isLoading) {
        this.classList.toggle("is-loading", isLoading);

        this.button.disabled =
          isLoading || this.currentVariant?.available === false;

        this.button.setAttribute("aria-busy", String(isLoading));
      }

      setStatus(message, isError = false) {
        if (!this.status) return;

        this.status.textContent = message;

        this.status.classList.toggle("is-error", isError);
      }

      getCartAddUrl() {
        const root = window.Shopify?.routes?.root || "/";

        const configuredUrl = window.routes?.cart_add_url;

        if (!configuredUrl) {
          return `${root}cart/add.js`;
        }

        if (/\.js(?:\?|$)/.test(configuredUrl)) {
          return configuredUrl;
        }

        return configuredUrl.replace(/\/cart\/add\/?$/, "/cart/add.js");
      }

      formatMoney(cents) {
        let formatted = "";

        if (window.Shopify?.formatMoney) {
          formatted = window.Shopify.formatMoney(
            cents,
            this.dataset.moneyFormat,
          );
        } else {
          const format = this.dataset.moneyFormat || "${{amount}}";

          const amount = (Number(cents || 0) / 100).toFixed(2);

          const amountNoDecimals = Math.round(
            Number(cents || 0) / 100,
          ).toString();

          const amountWithComma = amount.replace(".", ",");

          const amountNoDecimalsWithComma = amountNoDecimals.replace(
            /\B(?=(\d{3})+(?!\d))/g,
            ".",
          );

          formatted = format
            .replace("{{amount}}", amount)
            .replace("{{amount_no_decimals}}", amountNoDecimals)
            .replace("{{amount_with_comma_separator}}", amountWithComma)
            .replace(
              "{{amount_no_decimals_with_comma_separator}}",
              amountNoDecimalsWithComma,
            );
        }

        /*
         * money_format often includes entities like &pound;.
         * Decode to a real character so text-transform: uppercase
         * cannot break it into &POUND;.
         */
        return this.decodeHtmlEntities(formatted);
      }

      decodeHtmlEntities(value) {
        const div = document.createElement("div");

        div.innerHTML = String(value ?? "");

        return div.textContent || "";
      }

      escapeHtml(value) {
        const div = document.createElement("div");

        div.textContent = String(value ?? "");

        return div.innerHTML;
      }
    },
  );
}

if (!customElements.get("el-product-accordion")) {
  customElements.define(
    "el-product-accordion",
    class ElProductAccordion extends HTMLElement {
      connectedCallback() {
        if (this.dataset.ready === "true") return;
        this.dataset.ready = "true";

        this.trigger = this.querySelector("[data-el-accordion-trigger]");

        this.content = this.querySelector("[data-el-accordion-content]");

        if (!this.trigger || !this.content) return;

        this.handleClick = this.handleClick.bind(this);

        this.trigger.addEventListener("click", this.handleClick);

        const isOpen =
          this.hasAttribute("open") ||
          this.classList.contains("is-open") ||
          this.trigger.getAttribute("aria-expanded") === "true";

        this.setOpen(isOpen, {
          closeOthers: isOpen,
        });
      }

      disconnectedCallback() {
        this.trigger?.removeEventListener("click", this.handleClick);
      }

      handleClick() {
        const willOpen = !this.hasAttribute("open");

        this.setOpen(willOpen, {
          closeOthers: willOpen,
        });
      }

      closeOtherAccordions() {
        const accordionScope =
          this.closest("el-product-info") ||
          this.closest(".el-product__information") ||
          document;

        const openAccordions = accordionScope.querySelectorAll(
          "el-product-accordion[open]",
        );

        openAccordions.forEach((accordion) => {
          if (accordion === this) return;

          if (typeof accordion.setOpen !== "function") {
            return;
          }

          accordion.setOpen(false, {
            closeOthers: false,
          });
        });
      }

      setOpen(isOpen, options = {}) {
        const { closeOthers = false } = options;

        if (isOpen && closeOthers) {
          this.closeOtherAccordions();
        }

        this.toggleAttribute("open", isOpen);

        this.classList.toggle("is-open", isOpen);

        this.trigger.setAttribute("aria-expanded", String(isOpen));

        this.content.setAttribute("aria-hidden", String(!isOpen));
      }
    },
  );
}
